<script setup>
import { reactive } from 'vue';
import ActionDropdown from "../navigation/ActionDropdown";
import UserProfilePic from "@/components/users/UserProfilePic";
import EmailBounceAlert from "@/components/alerts/EmailBounceAlert.vue";
import {useAuthService} from "@/composables/useAuthService";
import {useValidation} from "@/composables/useValidation";
import {useMessaging} from "@/composables/useMessaging";
import { useRouter, useRoute } from 'vue-router';

const props = defineProps({
  showAdminClubId: {
    type: String,
    default: null
  },
  items: {
    type: Array,
    default: () => []
  }
});

const {user, isSuperUser, impersonateUser, isAuthenticated} = useAuthService();
const {isAdminControlledPlayer} = useValidation();
const { createConversation } = useMessaging();
const router = useRouter();
const route = useRoute();
const messageLoading = reactive({});
const getDropdown = (item) => {
  const actions = [
    {
      title: 'View profile',
      icon: 'mdi-account',
      route: {name: 'PlayerDetail', params: {playerId: item.id}}
    }
  ];
  if (isSuperUser.value && item.userId) {
    actions.push({
      title: 'Impersonate user',
      icon: 'mdi-lock',
      click: () => impersonateUser(item.userId)
    });
  }
  if(isAuthenticated.value && item.userId) {
    actions.push({
      title: 'Message user',
      icon: 'mdi-chat',
      loading: messageLoading[item.userId],
      click: async () => {
        if (!item.userId) return;
        try {
          messageLoading[item.userId] = true;
          const conversation = await createConversation([item.userId], 1, null);
          router.push({
            query: { ...route.query, conversationId: conversation.id, messaging: 'open' }
          });
        } catch (error) {
          console.error('Failed to create conversation:', error);
        } finally {
          messageLoading[item.userId] = false;
        }
      }
    });
  }
  return actions;
};
</script>

<template>
  <v-list class="pa-0 tl-card player-list">
    <template v-for="(item, index) in items" :key="item.id">
      <v-list-item :to="isSuperUser ? null : {name : 'PlayerDetail', params: {playerId : item.id}}">
        <div class="d-flex align-center flex-row py-2">
          <user-profile-pic size="medium" class="flex-wrap mr-3" :user="item"></user-profile-pic>
          <div class="flex-fill">
            <div class="player-name">
              {{ item.fullName }}
              <!-- No Squash Levels here: search results are not a place the level is shown (3 Nov 2026) -->
              <v-chip v-if="item.superAdmin" variant="flat" class="ml-2 tl-chip tl-chip--brand" size="x-small">
                Super admin
              </v-chip>

              <v-tooltip
                  text="This player is admin managed. You will need to contact the club admin to act on their behalf.">
                <template v-slot:activator="{ props }">
                  <v-chip v-bind="props" v-if="isAdminControlledPlayer(item)" variant="flat"
                          class="ml-2 tl-chip tl-chip--warn"
                          size="x-small">
                    Admin managed
                  </v-chip>
                </template>
              </v-tooltip>


              <v-chip v-if="item.id === user.id" variant="flat" class="ml-1 tl-chip tl-chip--brand" size="x-small">
                You
              </v-chip>

              <v-chip
                  v-if="showAdminClubId != null && item.adminFor.includes(showAdminClubId)"
                  class="ml-1 tl-chip"
                  variant="flat"
                  size="x-small"
              >
                Admin
              </v-chip>
            </div>
            <div class="player-sub">
              {{ item.email }}
              <email-bounce-alert
                  class="ml-2"
                  v-if="item.bouncedEmailReason"
                  :bounced-email-reason="item.bouncedEmailReason"
                  :bounced-email-created-at="item.bouncedEmailCreatedAt"
              />
            </div>
          </div>
          <action-dropdown icon="mdi-chevron-down" :items="getDropdown(item)" v-if="isSuperUser"></action-dropdown>
        </div>
      </v-list-item>

      <v-divider v-if="index < items.length - 1" :key="index"></v-divider>
    </template>
  </v-list>
</template>

<style scoped lang="scss">
/* row typography per mockups/redesign .comp-row (.t 13.5/600, .s 11.5 ink-3) */
.player-name {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 18px;
}

.player-sub {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  line-height: 16px;
  margin-top: 1px;
}

.player-list :deep(.v-divider) {
  border-color: rgb(var(--v-theme-borderLight));
  opacity: 1;
}

/* status chips — tint vocabulary (mirrors FixtureCell .tl-chip) */
.tl-chip {
  font-size: 10px;
  font-weight: 600;
  background: rgb(var(--v-theme-light)) !important;
  color: rgb(var(--v-theme-charcoal)) !important;
  border: 1px solid rgb(var(--v-theme-borderLight));

  &.tl-chip--brand {
    background: rgb(var(--v-theme-brandTint)) !important;
    color: rgb(var(--v-theme-brandInk)) !important; /* X152 (R73 L4): AA on the tint */
    border-color: transparent;
  }

  &.tl-chip--warn {
    background: rgb(var(--v-theme-warningTint)) !important;
    color: rgb(var(--v-theme-warningInk)) !important; /* X150 (R72 L3): AA on the tint */
    border-color: transparent;
  }
}
</style>
